import { useRouteError, useNavigate, isRouteErrorResponse } from 'react-router-dom';
import { AlertTriangle, RotateCcw, ArrowLeft, Home } from 'lucide-react';
import { isChunkLoadError, forceHardReload } from '@/utils/chunkReload';

/**
 * Route-level error boundary.
 *
 * Drops in as `errorElement` on each route in router.jsx. Catches:
 *   - Render-time errors thrown inside child components (the React error
 *     codes #130, #310, etc. that took the app down today)
 *   - 404s from React Router when a path doesn't match
 *   - Loader errors from `lazy: () => ...` chunk failures (the stale-deploy
 *     case — React Router swallows the import() rejection so the window
 *     error/unhandledrejection handlers in main.jsx never see it; the
 *     useEffect below auto-triggers a cache-busting reload when this kind
 *     of error lands here)
 *
 * Without this, React Router's default fallback says "Hey developer 👋"
 * with no way to recover — staff have to know to reload by hand. With
 * it, a single crashed route shows a contained card with a "Reload"
 * button and the user can navigate elsewhere via the AppShell nav
 * (which sits above the boundary).
 *
 * Production logging stub: window.__cervica_error_logger is a hook the
 * eventual Sentry/LogRocket integration can attach to. Until that's
 * wired the error still goes to console.error so it shows up in the
 * browser-side ECS logs.
 */
export default function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  const errorMessage = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error?.message || 'Something went wrong rendering this page.';

  const errorStack = error?.stack || null;
  const isChunkError = isChunkLoadError(error);

  // Chunk-load errors used to auto-trigger another reload here, but
  // that caused a flash-then-navigate cycle when the second attempt
  // also hit a stale chunk reference. The window-level handlers in
  // main.jsx already get ONE auto-reload attempt; if we land here it
  // means that attempt happened (or was budgeted out). At that point
  // the user gets a stable UI with a manual reload button.
  // (Intentionally left empty — do NOT call attemptHardReload() here.)

  // Log to console + optional external logger
  if (typeof console !== 'undefined') {
    console.error('[Cervica route error]', error);
  }
  if (typeof window !== 'undefined' && typeof window.__cervica_error_logger === 'function') {
    try { window.__cervica_error_logger(error); } catch { /* noop */ }
  }

  return (
    <div className="flex min-h-[400px] items-center justify-center p-6">
      <div className="max-w-lg w-full rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-900/[0.04]">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-red-50">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] font-bold text-gray-900">
              {isChunkError ? 'New version available — reload to continue' : 'This page hit an error'}
            </h2>
            <p className="mt-1 text-[13px] text-gray-600">{errorMessage}</p>
            <p className="mt-2 text-[11px] text-gray-500">
              {isChunkError
                ? 'Cervica was updated and your browser is holding a stale copy. Click reload to fetch the new version.'
                : 'The rest of Cervica is still working — use the navigation above to go somewhere else, or click reload to try this page again.'}
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button onClick={forceHardReload}
            className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white px-3 py-1.5 text-[12px] font-semibold">
            <RotateCcw className="h-3.5 w-3.5" /> Reload page
          </button>
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-3 py-1.5 text-[12px] font-medium">
            <ArrowLeft className="h-3.5 w-3.5" /> Go back
          </button>
          <button onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-3 py-1.5 text-[12px] font-medium">
            <Home className="h-3.5 w-3.5" /> Dashboard
          </button>
        </div>

        {/* Stack trace — only shown in dev builds. import.meta.env.DEV is
            statically replaced at build time so production bundles don't
            include the entire trace path. */}
        {import.meta.env.DEV && errorStack && (
          <details className="mt-4">
            <summary className="cursor-pointer text-[11px] font-medium text-gray-500 hover:text-gray-700">
              Show stack trace
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-gray-50 p-3 text-[10px] font-mono text-gray-700 whitespace-pre-wrap">
              {errorStack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
